import type { DragState, ContractDraft, CellState } from './types';
import { getDayPrice, toYmd, addDays } from './pricing';
import { openContractModal } from './modals';

type CellStateGetter = (unitId: number, dayIdx: number) => CellState;
type RedrawCallback = (drag: DragState | null) => void;

let _dragging: DragState | null = null;
let _getState: CellStateGetter | null = null;
let _getDays: () => Date[] = () => [];
let _getRowData: (unitId: number) => { accomId: number } | null = () => null;
let _redraw: RedrawCallback = () => {};
let _onSelect: ((draft: ContractDraft) => void) | null = null;
let _listenerRegistered = false;

export function initDrag(opts: {
    getState: CellStateGetter;
    getDays: () => Date[];
    getRowData: (unitId: number) => { accomId: number } | null;
    redraw: RedrawCallback;
    onSelect?: (draft: ContractDraft) => void;
}): void {
    _getState = opts.getState;
    _getDays = opts.getDays;
    _getRowData = opts.getRowData;
    _redraw = opts.redraw;
    _onSelect = opts.onSelect ?? null;

    if (!_listenerRegistered) {
        document.addEventListener('mouseup', onMouseUp);
        _listenerRegistered = true;
    }
}

export function cancelDrag(): void {
    _dragging = null;
    _redraw(null);
}

export function onCellMouseDown(unitId: number, dayIdx: number, state: CellState): void {
    if (state !== 'free') return;
    _dragging = { unitId, startIdx: dayIdx, currIdx: dayIdx };
    _redraw(_dragging);
}

export function onCellMouseEnter(unitId: number, dayIdx: number): void {
    if (!_dragging || _dragging.unitId !== unitId) return;
    _dragging = { ..._dragging, currIdx: dayIdx };
    _redraw(_dragging);
}

function onMouseUp(): void {
    if (!_dragging || !_getState) { _dragging = null; _redraw(null); return; }

    const a = Math.min(_dragging.startIdx, _dragging.currIdx);
    const b = Math.max(_dragging.startIdx, _dragging.currIdx);
    const { unitId } = _dragging;
    _dragging = null;
    _redraw(null);

    let ok = true;
    for (let i = a; i <= b; i++) {
        if (_getState(unitId, i) !== 'free') { ok = false; break; }
    }
    if (!ok) return;

    const days = _getDays();
    const rowData = _getRowData(unitId);
    if (!rowData) return;

    const fromY = toYmd(days[a]);
    const toY = toYmd(addDays(days[b], 1));
    const nights = b - a + 1;
    const prices: Array<{ date: string; price: number | null }> = [];
    let total = 0;
    for (let i = a; i <= b; i++) {
        const p = getDayPrice(unitId, toYmd(days[i]));
        prices.push({ date: toYmd(days[i]), price: p });
        total += p ?? 0;
    }

    const draft: ContractDraft = {
        unitId,
        accomId: rowData.accomId,
        from: fromY,
        to: toY,
        nights,
        prices,
        total,
    };
    if (_onSelect) _onSelect(draft);
    else openContractModal(draft);
}
